import { type FC } from 'react';

import { classificationTypes } from '@/components/Studies/Treatments/Treatment.utils';
import Link from '@/components/UI/Link';
import Table from '@/components/UI/Table';
import { mergeColumnVisibility } from '@/components/UI/Table/Reusable/APITable/APITable.utils';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { renderMetadataValue } from '@/helpers';
import { _isNotBlank, _isNotEmpty, _isNil, _notNil } from '@/littledash';
import type { ClassificationType } from '@/model/Treatment.model';
import type { UserTableViewTableName } from '@/model/UserTableView.model';
import { useUserTableViews } from '@/utils/userTableSettings/UserTableSettingsContext';

import type { OverviewTreatmentRow } from '../../OverviewAccordion.model';
import OverviewGroupChips from '../OverviewGroupChips';
import overviewTableStyles from '../OverviewSectionTable.module.scss';

interface OverviewTreatmentsTableProps {
  rows: Array<OverviewTreatmentRow>;
  type: ClassificationType;
  studyId?: string;
  onLabelClick?: (row: OverviewTreatmentRow) => void;
}

export const emptyValue = '-';

const formatOptionalText = (value: string | undefined): string => (_isNotBlank(value) ? value : emptyValue);

/**
 * `renderMetadataValue` returns an empty string for blank text and an empty multi-select, so the
 * placeholder is applied to the formatted text. Stringifying keeps numeric values such as zero
 * visible.
 */
export const formatMetadataValue = (value: string | number | Array<string> | null, fieldType: string): string => {
  if (_isNil(value)) {
    return emptyValue;
  }
  const rendered = renderMetadataValue({ value, field_type: fieldType });
  return String(rendered) === '' ? emptyValue : rendered;
};

const defaultColumnVisibility = (columns: Array<TanStackColumnDef<OverviewTreatmentRow>>): Record<string, boolean> =>
  columns.reduce<Record<string, boolean>>((acc, column) => {
    if (column.isVisible === false) {
      acc[column.id as string] = false;
    }
    return acc;
  }, {});

/**
 * Read-only treatments/diseases table built like the matching study-groups tab: the same
 * column ids and default visibility, overridden by the user's active view for that table.
 * The server already returns only the metadata columns that view turns on.
 */
const OverviewTreatmentsTable: FC<OverviewTreatmentsTableProps> = ({ rows, type, studyId, onLabelClick }) => {
  const { getActiveSettings } = useUserTableViews();
  const tableName: UserTableViewTableName = type === 'disease_induction' ? 'diseases' : 'treatments';
  const activeColumnVisibility = getActiveSettings(tableName)?.column_visibility;

  const metadataColumns: Array<TanStackColumnDef<OverviewTreatmentRow>> = (rows[0]?.metadata ?? []).map(
    ({ slug, title }) => ({
      id: `meta_${slug}`,
      header: title,
      cell: ({ row: { original } }) => {
        const field = original.metadata.find((metadataField) => metadataField.slug === slug);
        return _isNil(field) ? emptyValue : formatMetadataValue(field.value, field.field_type);
      },
    })
  );

  const columns: Array<TanStackColumnDef<OverviewTreatmentRow>> = [
    {
      id: 'treatment_label',
      header: 'Label',
      accessorKey: 'label',
      cell: ({ row: { original } }) =>
        _notNil(onLabelClick) ? (
          <Link
            className="link"
            data-testid={`edit-treatment-${original.api_id}`}
            openModal
            onClick={() => onLabelClick(original)}
          >
            {original.label}
          </Link>
        ) : (
          original.label
        ),
    },
    {
      id: 'treatment_name',
      header: 'Name',
      accessorKey: 'type_name',
      cell: ({
        row: {
          original: { type_name: typeName },
        },
      }) => formatOptionalText(typeName),
    },
    {
      id: 'treatment_classification',
      header: 'Classification',
      accessorKey: 'classification',
      isVisible: type === 'treatment',
      cell: ({
        row: {
          original: { classification },
        },
      }) => classificationTypes[classification].title,
    },
    {
      id: 'dose_concentration',
      header: 'Dose',
      accessorKey: 'dose',
      cell: ({
        row: {
          original: { dose },
        },
      }) => formatOptionalText(dose),
    },
    {
      id: 'dose_volume',
      header: 'Dose volume',
      accessorKey: 'dose_volume',
      cell: ({
        row: {
          original: { dose_volume: doseVolume },
        },
      }) => formatOptionalText(doseVolume),
    },
    {
      id: 'stock_concentration',
      header: 'Stock Concentration',
      accessorKey: 'stock',
      cell: ({
        row: {
          original: { stock },
        },
      }) => formatOptionalText(stock),
    },
    {
      id: 'volume',
      header: 'Volume',
      accessorKey: 'volume',
      cell: ({
        row: {
          original: { volume },
        },
      }) => formatOptionalText(volume),
    },
    ...(rows.some((row) => _isNotEmpty(row.group_api_ids))
      ? [
          {
            id: 'groups',
            header: 'Groups',
            accessorKey: 'group_api_ids',
            cell: ({
              row: {
                original: { group_api_ids: groupApiIds, label },
              },
            }: {
              row: { original: OverviewTreatmentRow };
            }) => <OverviewGroupChips groupApiIds={groupApiIds} groupDetails={{ title: label, studyId }} />,
          } satisfies TanStackColumnDef<OverviewTreatmentRow>,
        ]
      : []),
    ...metadataColumns,
  ];

  const columnVisibility = mergeColumnVisibility(activeColumnVisibility, defaultColumnVisibility(columns));
  const visibleColumns = columns
    .filter((column) => columnVisibility[column.id as string] !== false)
    .map((column) => ({ ...column, isVisible: true }));

  return <Table data={rows} columns={visibleColumns} maxHeightClassName={overviewTableStyles.maxHeight} />;
};

export default OverviewTreatmentsTable;
